import { SURFACE_PLOT_IDS, type BuildingType, type BuildingZone, type Planet } from '@kaeliux/shared';
import { ZONE_ORDER, ZONE_WIDTH, projectTile, type SurfaceAvoidRect, type SurfacePropArea } from '../../utils/surfaceScene';
import { BUILDING_SPRITE_HEIGHT } from './buildingSpriteSize';

/**
 * World position of one buildable plot on the planet surface (DS-28).
 * Position/depth only — the plot's own identity (`SURFACE_PLOT_IDS`) and
 * every building's name/cost/prerequisites live in `packages/shared` and
 * must be read from there, not re-declared here.
 *
 * DS-25 bound every position to a fixed `BuildingType` in declaration
 * order, so the building that ended up on a given position had nothing to
 * do with which one the player tapped (issue #225: "I build metal mine on
 * lot 4, it appeared on lot 1"). DS-28 replaced that: a plot is just a
 * position now — which building (if any) occupies it is read live from
 * `Planet.buildings[type].plotId` by `PlanetSurface`, not decided here.
 */
export interface SurfacePlot {
    id: string;
    zone: BuildingZone;
    worldX: number;
    depth: number;
}

/**
 * Inset from each zone's edges before the first/last plot, in px. Keeps
 * tiles off the zone-boundary marker and stage vignette.
 */
const ZONE_PLOT_MARGIN = 140;

/**
 * Depths cycled across a zone's plots so neighbouring tiles alternate
 * foreground/background scale rather than lining up flat — `projectTile`
 * (DS-13) turns a lower depth into a larger, higher-stacked tile. Long
 * enough to cover the largest zone (industry, `SURFACE_PLOT_IDS.industry`)
 * without repeating back-to-back.
 */
const DEPTH_CYCLE = [0.1, 0.5, 0.02, 0.42, 0.2, 0.48, 0.3, 0.15, 0.38];

/** Evenly-spaced positions for one zone's plots, inset by `ZONE_PLOT_MARGIN` on each side. */
function zonePositions(zone: BuildingZone, zoneIndex: number, plotIds: readonly string[]): SurfacePlot[] {
    const usableWidth = ZONE_WIDTH - ZONE_PLOT_MARGIN * 2;
    const spacing = plotIds.length > 1 ? usableWidth / (plotIds.length - 1) : 0;
    const zoneStart = zoneIndex * ZONE_WIDTH;

    return plotIds.map((id, index) => ({
        id,
        zone,
        worldX: zoneStart + ZONE_PLOT_MARGIN + spacing * index,
        depth: DEPTH_CYCLE[index % DEPTH_CYCLE.length] ?? 0,
    }));
}

/**
 * One position per `SURFACE_PLOT_IDS` entry (DS-28) — every plot the server
 * will validate a construct against gets exactly one position here, keyed
 * by the same id, so nothing the server accepts is ever unreachable from
 * the surface.
 */
export const SURFACE_LAYOUT: SurfacePlot[] = ZONE_ORDER.flatMap((zone, zoneIndex) =>
    zonePositions(zone, zoneIndex, SURFACE_PLOT_IDS[zone]),
);

/**
 * Which built building (if any) occupies a given plot (DS-28) — the plot
 * itself no longer knows in advance, so both `PlanetSurface` (rendering
 * every plot) and `BuildingPopover` (resolving the tapped one) look this up
 * live from `Planet.buildings[type].plotId` instead.
 */
export function findPlotOccupant(planet: Planet, plotId: string): BuildingType | undefined {
    return (Object.keys(planet.buildings) as BuildingType[]).find(
        (type) => (planet.buildings[type]?.level ?? 0) > 0 && planet.buildings[type]?.plotId === plotId,
    );
}

/**
 * Preferred horizontal clearance either side of a plot's centre (DS-29 issue
 * #227) — a product call, not derived from any sprite measurement. Only
 * usable as-is where plots have room: `industry`'s 9 plots sit just ~120px
 * apart (`usableWidth / (count - 1)`, above), and military's widest gap
 * approaches 200px, so a flat 90px half-width (180px wide) either swallows a
 * dense zone's entire span with overlapping rects — leaving no ground
 * anywhere for a prop, the opposite of "props appear around the buildings"
 * — or is needlessly cautious in a sparse one. `surfacePlotAvoidRects` below
 * shrinks it per plot instead of using it flat.
 */
const PLOT_AVOID_PREFERRED_HALF_WIDTH_PX = 110;
/**
 * Never shrunk smaller than this, regardless of how close a neighbour sits.
 *
 * DS-29 issue #227 round 3: a prop that fit in the leftover gap between two
 * plots still visually collided with a building's own *name label* — text
 * like "Mineral Extractor"/"Recycling Plant" (the names when this was
 * measured) renders wider than the sprite above it, roughly 150-170px
 * (~75-85px half-width). The longest names since #206 ("Poly-Carbon
 * Extractor", "Engineering Facility") are wider still, so treat 85 as a
 * floor. In `industry` (~120px apart) and `resource` (~137px apart) that
 * already fully closes the gap
 * between plots — intentional: a sliver of "open" ground narrower than a
 * label was never actually safe to place a prop in. `military` (~192px
 * apart) is the only zone wide enough to still leave a real gap.
 *
 * Round 7 briefly widened this to the full sprite box
 * (`BUILDING_SPRITE_WIDTH / 2` = 89px, wider than the label estimate) after
 * a reported prop/plot overlap — but the actual, verified root cause of
 * that overlap was the *prop's own* footprint estimate
 * (`PROP_WIDTH_TO_HEIGHT_ESTIMATE`) undershooting badly, not this floor
 * being too small; widening it back to 89 measurably shrank `military`'s
 * only remaining interior gap (22px → 14px) for no benefit, contributing to
 * round 8's "terrain is almost empty, props only appear at zone borders".
 * Reverted to the original label-based 85.
 */
const PLOT_AVOID_MIN_HALF_WIDTH_PX = 85;
/** Real ground kept clear between two adjacent plots' rects, however tightly they're spaced. */
const PLOT_AVOID_NEIGHBOR_GAP_PX = 24;
/**
 * Extra headroom above `BUILDING_SPRITE_HEIGHT` (DS-29 issue #227, round 2:
 * "still in conflict with the buildings"). A sprite's real silhouette isn't
 * always a clean rectangle up to its box cap — a tall thin antenna/arm (Hydrogen
 * Refinery's mast, Engineering Facility's raised arm) can read as taller than the box
 * suggests once anti-aliased edges and the sprite's own drop-shadow filter
 * are counted, plus the name label rendered just below every sprite. This is
 * a flat safety margin, not derived from any per-building measurement.
 */
const PLOT_AVOID_VERTICAL_BUFFER_PX = 48;

/** Distance to the closest other plot in world X — same-zone plots are always nearer than any other zone's, so this needs no zone filtering. */
function nearestNeighborDistance(slot: SurfacePlot): number {
    let nearest = Infinity;
    for (const other of SURFACE_LAYOUT) {
        if (other === slot) continue;
        nearest = Math.min(nearest, Math.abs(other.worldX - slot.worldX));
    }
    return nearest;
}

/**
 * One ground-clutter exclusion rectangle per plot (DS-29 issue #227: props
 * were landing on top of empty-plot outlines, real buildings, and their cast
 * shadows). Covers every plot regardless of whether it's currently built —
 * an empty plot's dashed outline is ground furniture too, and this stays a
 * pure function of the static layout, not live planet data.
 *
 * The horizontal half-width shrinks to fit however close this plot's nearest
 * neighbour actually is, capped at `PLOT_AVOID_PREFERRED_HALF_WIDTH_PX` and
 * floored at `PLOT_AVOID_MIN_HALF_WIDTH_PX` — so a dense zone (industry)
 * still leaves real gaps between plots, while a sparse one (military) still
 * gets the fuller preferred clearance.
 *
 * The vertical reach is a flat full-height column — from a bit above the
 * tallest possible sprite (`BUILDING_SPRITE_HEIGHT`, worst case, plus a flat
 * buffer) down through the *entire* rest of the ground band, not just a
 * narrow band near this plot's own `projectTile`-derived contact line. An
 * earlier version scaled the vertical reach by this plot's own depth, which
 * sounded right but wasn't: a *different* plot's building/label renders at
 * its own, unrelated screen position depending on its own depth, and a prop
 * whose `y` happens to land somewhere else in the ground band could still
 * render tall enough on screen (via `propDepthScale`) to visually collide
 * with a plot far from that prop's own contact line — confirmed by a real
 * screenshot (issue #227 round 3: "still in conflict with the buildings")
 * showing a foreground prop overlapping a background plot's name label.
 * Excluding the plot's whole column, not just a slice of it, removes that
 * whole class of mismatch.
 */
export function surfacePlotAvoidRects(groundTop: number, groundHeight: number): SurfaceAvoidRect[] {
    return SURFACE_LAYOUT.map((slot) => {
        const halfWidth = Math.min(
            PLOT_AVOID_PREFERRED_HALF_WIDTH_PX,
            Math.max(PLOT_AVOID_MIN_HALF_WIDTH_PX, nearestNeighborDistance(slot) / 2 - PLOT_AVOID_NEIGHBOR_GAP_PX / 2),
        );
        return {
            left: slot.worldX - halfWidth,
            right: slot.worldX + halfWidth,
            top: groundTop - BUILDING_SPRITE_HEIGHT - PLOT_AVOID_VERTICAL_BUFFER_PX,
            bottom: groundTop + groundHeight,
        };
    });
}

/** Horizontal half-width of a curated prop pocket (`surfacePropAreas`) around its anchor plot's `worldX` — narrow enough that even industry's tightest ~120px plot spacing leaves each pocket its own strip, not overlapping its neighbour's. */
const PROP_AREA_HALF_WIDTH_PX = 48;

/** Target height of a curated pocket's vertical band, px — shrunk to fit when the anchor's own geometry leaves less room than this. */
const PROP_AREA_BAND_HEIGHT_PX = 70;
/** Gap kept between a pocket's near edge and the horizon / the ground band's bottom edge. */
const PROP_AREA_EDGE_INSET_PX = 14;
/**
 * Worst-case rendered heights of the two tiles that can stand on a plot, px.
 * `BUILDING_SPRITE_HEIGHT` alone undercounts both, because the name label and
 * level badge stack below the sprite inside the same transformed box.
 *
 * Measured off real rendered tiles: an empty plot is a fixed 122.8px (dashed
 * outline + "Empty Plot" caption), and a built tile ran 172.8-220.8px across
 * the building set. Rounded up for a wrapped name, and kept separate because
 * the two kinds scale about different origins (see `verticalBand`), so one
 * shared ceiling cannot describe both. A single generous 300px number used to
 * stand in for both here, which — once the transform maths was corrected —
 * reserved so much room above a deep plot that every background pocket
 * collapsed to zero height.
 */
const BUILT_TILE_WORST_CASE_HEIGHT_PX = 250;
/**
 * Defensive rather than currently reachable: `250 * s` already beats
 * `75 * (1 + s)` for every `s > 0.4286`, and `DEPTH_CYCLE`'s deepest plot only
 * reaches `s = 0.69`, so the built tile wins the `max()` at every depth the
 * layout actually uses. It stays because the two kinds scale about different
 * origins and a shallower depth cycle (or a shorter built tile) would flip it.
 */
const PLOT_TILE_WORST_CASE_HEIGHT_PX = 150;
/** Extra gap kept beyond the worst-case sprite/label edge, on top of those heights already being worst cases. */
const PROP_AREA_SPRITE_CLEARANCE_PX = 22;
const PROP_AREA_LABEL_CLEARANCE_PX = 22;

type PropAreaBand = 'far' | 'above' | 'below' | 'near';

/** The plot at 1-based lot number `lotNumber` within `zone` — same id format `SURFACE_PLOT_IDS` builds (`${zone}-${n}`). */
function plotAt(zone: BuildingZone, lotNumber: number): SurfacePlot {
    const id = `${zone}-${lotNumber}`;
    const plot = SURFACE_LAYOUT.find((slot) => slot.id === id);
    if (!plot) {
        throw new Error(`surfacePropAreas: no plot ${id} in SURFACE_LAYOUT`);
    }
    return plot;
}

/**
 * A pocket's vertical span, derived from its own anchor plot's *real*
 * projected geometry rather than a flat fraction of `groundHeight` (DS-29
 * issue #227 round 9 — see `surfacePropAreas`' own doc comment for why a
 * flat fraction doesn't work). `anchorTop`/`anchorScale` are this specific
 * plot's own `projectTile(plot.depth, ...)` output: a plot's depth swings
 * its sprite between small-and-near-the-horizon (far/background) and
 * tall-and-near-the-camera (close/foreground), so how much clear room sits
 * above vs. below any *particular* plot varies with that plot's own depth,
 * not a scene-wide constant.
 *
 * Two different tiles can stand on a plot and they do NOT transform the same
 * way, so this reserves the union of both. Every tile is positioned at
 * `top: anchorTop` and carries `transform: translate(-50%, -100%)
 * scale(scale)`, but they disagree on the origin that scale pivots around:
 *
 * - A built tile (`BuildingTile`) sets `transform-origin: bottom center`.
 *   Scaling about the bottom-centre point cannot move the bottom edge, so it
 *   lands on `anchorTop` exactly and the top edge sits at `anchorTop - H*s`.
 * - An empty plot (`PlotTile`) leaves the origin at its default centre, which
 *   does move the bottom edge: `anchorTop - (H/2)*(1 - s)`, top edge
 *   `anchorTop - (H/2)*(1 + s)`.
 *
 * Both were measured off real `getBoundingClientRect()` values across the
 * full `DEPTH_CYCLE` scale range (0.69..0.99); each formula reproduces its
 * own tile kind to within 0.02px and misses the other kind by up to 33px.
 * Taking the union for a shared `H`: the centre-origin top is always the
 * higher of the two (their difference is `(H/2)*(s - 1)`, never positive for
 * `s <= 1`) and the bottom-centre bottom is always the lower, so:
 *
 *   reserved top    = anchorTop - max(H_built * scale, (H_plot / 2) * (1 + scale))
 *   reserved bottom = anchorTop
 *
 * Each kind brings its own worst-case height, because they differ by ~100px
 * and one shared ceiling over-reserves for whichever kind is shorter.
 *
 * "above"/"far" pockets share the span between the horizon and this
 * anchor's own worst-case rendered top — "far" claims the part of that span
 * closest to the horizon, plain "above" the part closest to (but still
 * clearing) the sprite. "below"/"near" pockets mirror this below the
 * anchor's worst-case rendered bottom: plain "below" claims the part
 * closest to the anchor, "near" ("up close") the part closest to the
 * camera.
 */
function verticalBand(
    band: PropAreaBand,
    groundTop: number,
    groundHeight: number,
    anchorTop: number,
    anchorScale: number,
): { top: number; bottom: number; reserved: { top: number; bottom: number } } {
    if (band === 'far' || band === 'above') {
        const worstCaseSpriteTop = anchorTop - Math.max(
            BUILT_TILE_WORST_CASE_HEIGHT_PX * anchorScale,
            (PLOT_TILE_WORST_CASE_HEIGHT_PX / 2) * (1 + anchorScale),
        );
        const spanTop = groundTop + PROP_AREA_EDGE_INSET_PX;
        const spanBottom = Math.max(spanTop, worstCaseSpriteTop - PROP_AREA_SPRITE_CLEARANCE_PX);
        const height = Math.min(PROP_AREA_BAND_HEIGHT_PX, spanBottom - spanTop);
        const reserved = { top: worstCaseSpriteTop - PROP_AREA_SPRITE_CLEARANCE_PX, bottom: anchorTop + PROP_AREA_LABEL_CLEARANCE_PX };
        return band === 'far'
            ? { top: spanTop, bottom: spanTop + height, reserved }
            : { top: spanBottom - height, bottom: spanBottom, reserved };
    }
    const worstCaseSpriteBottom = anchorTop;
    const spanTop = worstCaseSpriteBottom + PROP_AREA_LABEL_CLEARANCE_PX;
    const spanBottom = Math.max(spanTop, groundTop + groundHeight - PROP_AREA_EDGE_INSET_PX);
    const height = Math.min(PROP_AREA_BAND_HEIGHT_PX, spanBottom - spanTop);
    const worstCaseSpriteTop = anchorTop - Math.max(
        BUILT_TILE_WORST_CASE_HEIGHT_PX * anchorScale,
        (PLOT_TILE_WORST_CASE_HEIGHT_PX / 2) * (1 + anchorScale),
    );
    const reserved = { top: worstCaseSpriteTop - PROP_AREA_SPRITE_CLEARANCE_PX, bottom: spanTop };
    return band === 'below'
        ? { top: spanTop, bottom: spanTop + height, reserved }
        : { top: spanBottom - height, bottom: spanBottom, reserved };
}

/**
 * Twelve curated prop pockets (DS-29 issue #227 round 9), replacing every
 * earlier round's free-scatter-plus-avoid-rects search. The user hand-picked
 * these directly rather than tuning another collision parameter:
 *
 * > Above lot 1 in resource zone / below lot 6 up close in resource zone /
 * > above lot 8 in distance / above lot 1 in industry zone in distance /
 * > above lot 3 in industry zone in distance / below lot 6 in industry zone
 * > up close / below lot 9 in industry zone / above lot 1 in military zone
 * > in the distance / below lot 2 in military zone up close / above lot 3
 * > in military zone / below lot 4 in military zone / below lot 6 in
 * > military zone — "populate the props randomly in these areas."
 *
 * Each pocket is a small box centred on its named plot's `worldX`
 * (`PROP_AREA_HALF_WIDTH_PX` either side) and a `verticalBand` (above)
 * derived from that same plot's own real projected geometry. A first
 * version used a flat fraction of `groundHeight` for the vertical band
 * instead — reads fine at a glance, but a real Playwright DOM measurement
 * (`getBoundingClientRect` on both the prop and every building tile)
 * caught it rendering several pockets directly on top of their own anchor's
 * sprite: a fixed fraction has no idea *that specific* plot's depth put its
 * sprite somewhere else on screen than the "typical" plot the fraction was
 * eyeballed against. Deriving the band from the anchor's own
 * `projectTile` output instead fixes that by construction, verified by
 * re-running the same DOM measurement to zero overlaps.
 *
 * Its `avoidRects` is every *other* plot's exclusion rect from
 * `surfacePlotAvoidRects` — deliberately excluding its own anchor plot's,
 * since sitting near that plot is the entire point; the anchor itself is
 * instead kept clear by `verticalBand`'s own geometry, not by a rect.
 */
export function surfacePropAreas(groundTop: number, groundHeight: number): SurfacePropArea[] {
    const plotRects = surfacePlotAvoidRects(groundTop, groundHeight);

    const makeArea = (zone: BuildingZone, lotNumber: number, band: PropAreaBand): SurfacePropArea => {
        const plot = plotAt(zone, lotNumber);
        const anchorIndex = SURFACE_LAYOUT.indexOf(plot);
        const anchorProjection = projectTile(plot.depth, groundTop, groundHeight);
        const { top, bottom, reserved } = verticalBand(band, groundTop, groundHeight, anchorProjection.top, anchorProjection.scale);
        return {
            zone,
            left: plot.worldX - PROP_AREA_HALF_WIDTH_PX,
            right: plot.worldX + PROP_AREA_HALF_WIDTH_PX,
            top,
            bottom,
            avoidRects: plotRects.filter((_, index) => index !== anchorIndex),
            anchorReserved: reserved,
        };
    };

    // A pocket whose anchor leaves it no room is dropped here rather than
    // handed out with zero height: on a short stage a deep plot's tile
    // genuinely reaches the horizon, so there is nowhere above it for a prop
    // to stand, and a zero-height band would otherwise be sampled at exactly
    // the point the tile occupies. Callers therefore get "the pockets that
    // fit at this size", which is fewer than twelve on short stages.
    return [
        makeArea('resource', 1, 'above'),
        makeArea('resource', 6, 'near'),
        makeArea('resource', 8, 'far'),
        makeArea('industry', 1, 'far'),
        makeArea('industry', 3, 'far'),
        makeArea('industry', 6, 'near'),
        makeArea('industry', 9, 'below'),
        makeArea('military', 1, 'far'),
        makeArea('military', 2, 'near'),
        makeArea('military', 3, 'above'),
        makeArea('military', 4, 'below'),
        makeArea('military', 6, 'below'),
    ].filter((area) => area.bottom > area.top);
}

